import React, { useState, useMemo, useEffect } from 'react';
import './ReassignTaskModal.css';

const ReassignTaskModal = ({ task, users, onClose, onReassign }) => {
    const [selectedIds, setSelectedIds] = useState(() => {
        const ids = task?.assignedTo?.map(a => (typeof a === 'object' ? a._id : a)) || [];
        return ids.length ? ids : [];
    });
    const [reason, setReason] = useState('');
    const [search, setSearch] = useState('');
    const [showUserSlideOver, setShowUserSlideOver] = useState(false);

    // Prevent body scroll when slide-over is open
    useEffect(() => {
        if (showUserSlideOver) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [showUserSlideOver]);

    const getFilteredUsers = () => {
        if (!users || typeof users !== 'object') return {};
        
        if (!search.trim()) return users;
        
        const searchLower = search.toLowerCase();
        const filtered = {};
        
        Object.keys(users).forEach(department => {
            const filteredDeptUsers = users[department].filter(u => 
                (u.fullName || '').toLowerCase().includes(searchLower) ||
                (u.username || '').toLowerCase().includes(searchLower) ||
                (u.role || '').toLowerCase().includes(searchLower) ||
                department.toLowerCase().includes(searchLower)
            );
            if (filteredDeptUsers.length > 0) {
                filtered[department] = filteredDeptUsers;
            }
        });
        
        return filtered;
    };

    const toggleUser = (id) => {
        setSelectedIds(prev =>
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        );
    };

    const handleSelectAllUsers = () => {
        const allUserIds = Object.values(users).flat().map(u => u._id);
        setSelectedIds(allUserIds);
    };

    const handleDeselectAllUsers = () => {
        setSelectedIds([]);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (selectedIds.length === 0) {
            alert('Please select at least one assignee.');
            return;
        }
        onReassign(selectedIds, reason.trim() || undefined);
    };

    const currentAssignees = Array.isArray(task?.assignedToName)
        ? task.assignedToName.join(', ')
        : (task?.assignedToName || '—');

    return (
        <>
            <div className="reassign-modal-overlay" onClick={onClose}>
                <div className="reassign-modal-content" onClick={e => e.stopPropagation()}>
                    {/* Header */}
                    <div className="reassign-modal-header">
                        <div className="reassign-modal-title-section">
                            <h2>Reassign Task</h2>
                            <button 
                                className="reassign-modal-close-btn" 
                                onClick={onClose}
                                aria-label="Close"
                            >
                                ✕
                            </button>
                        </div>
                    </div>

                    {/* Body */}
                    <div className="reassign-modal-body">
                        {task && (
                            <>
                                <div className="reassign-task-info">
                                    <div className="reassign-task-title">{task.title}</div>
                                    <div className="reassign-current-assignees">
                                        Current assignees: <strong>{currentAssignees}</strong>
                                    </div>
                                </div>

                                <form onSubmit={handleSubmit}>
                                    <div className="form-group">
                                        <label>New assignees *</label>
                                        <div 
                                            className="reassign-user-select-trigger"
                                            onClick={() => setShowUserSlideOver(true)}
                                        >
                                            <span className="user-select-text">
                                                {selectedIds.length === 0 
                                                    ? 'Click to select users...' 
                                                    : `${selectedIds.length} user(s) selected`
                                                }
                                            </span>
                                            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label>Reason (optional)</label>
                                        <textarea
                                            value={reason}
                                            onChange={e => setReason(e.target.value)}
                                            rows={3}
                                            placeholder="e.g. Handing over to team lead"
                                            className="form-control"
                                        />
                                    </div>
                                </form>
                            </>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="reassign-modal-footer">
                        <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
                        <button type="button" onClick={handleSubmit} className="btn-primary">Reassign Task</button>
                    </div>
                </div>
            </div>

            {/* User Selection Slide-Over Panel */}
            {showUserSlideOver && (
                <div className="reassign-slideover-overlay" onClick={() => setShowUserSlideOver(false)}>
                    <div className="reassign-slideover-panel" onClick={(e) => e.stopPropagation()}>
                        <div className="slideover-header">
                            <div className="slideover-title-section">
                                <h2>Select Users</h2>
                                <span className="selected-count">
                                    {selectedIds.length} selected
                                </span>
                            </div>
                            <button 
                                className="slideover-close-btn" 
                                onClick={() => setShowUserSlideOver(false)}
                                aria-label="Close"
                            >
                                ✕
                            </button>
                        </div>

                        <div className="slideover-search">
                            <input
                                type="text"
                                placeholder="Search by name, role, or department..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="slideover-search-input"
                            />
                            {search && (
                                <button 
                                    className="search-clear-btn"
                                    onClick={() => setSearch('')}
                                >
                                    ✕
                                </button>
                            )}
                        </div>

                        <div className="slideover-actions">
                            <button 
                                className="btn-select-all"
                                onClick={handleSelectAllUsers}
                            >
                                Select All
                            </button>
                            <button 
                                className="btn-deselect-all"
                                onClick={handleDeselectAllUsers}
                            >
                                Deselect All
                            </button>
                        </div>

                        <div className="slideover-content">
                            {Object.keys(getFilteredUsers()).sort().map(department => {
                                const deptUsers = getFilteredUsers()[department];
                                if (!deptUsers || deptUsers.length === 0) return null;

                                const allDeptSelected = deptUsers.every(u => selectedIds.includes(u._id));
                                const someDeptSelected = deptUsers.some(u => selectedIds.includes(u._id)) && !allDeptSelected;
                                
                                return (
                                    <div key={department} className="slideover-department">
                                        <div className="department-header">
                                            <label className="department-checkbox-label">
                                                <input
                                                    type="checkbox"
                                                    checked={allDeptSelected}
                                                    ref={input => {
                                                        if (input) input.indeterminate = someDeptSelected;
                                                    }}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setSelectedIds(prev => {
                                                                const newIds = [...prev];
                                                                deptUsers.forEach(u => {
                                                                    if (!newIds.includes(u._id)) {
                                                                        newIds.push(u._id);
                                                                    }
                                                                });
                                                                return newIds;
                                                            });
                                                        } else {
                                                            setSelectedIds(prev => 
                                                                prev.filter(id => !deptUsers.find(u => u._id === id))
                                                            );
                                                        }
                                                    }}
                                                />
                                                <span className="department-name">{department}</span>
                                                <span className="department-count">({deptUsers.length})</span>
                                            </label>
                                        </div>
                                        <div className="department-users">
                                            {deptUsers.map(u => (
                                                <label key={u._id} className="user-checkbox-label">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedIds.includes(u._id)}
                                                        onChange={() => toggleUser(u._id)}
                                                    />
                                                    <div className="user-info">
                                                        <span className="user-name">{u.fullName || u.username}</span>
                                                        <span className="user-role">{u.role}</span>
                                                    </div>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                            {Object.keys(getFilteredUsers()).length === 0 && (
                                <div className="no-results">
                                    <p>No users found matching your search.</p>
                                </div>
                            )}
                        </div>

                        <div className="slideover-footer">
                            <button 
                                className="btn-secondary"
                                onClick={() => setShowUserSlideOver(false)}
                            >
                                Cancel
                            </button>
                            <button 
                                className="btn-primary"
                                onClick={() => setShowUserSlideOver(false)}
                                disabled={selectedIds.length === 0}
                            >
                                Done ({selectedIds.length} selected)
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ReassignTaskModal;
